import { useCallback, useEffect, useState } from "react";
import { Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { adminApi, brl, errMsg, fmtDate } from "@/lib/api";

const KIND = { tenant: "Cliente", partner: "Parceiro" };

function PayoutRow({ p, onMark }) {
  const copy = () => navigator.clipboard.writeText(p.pix_key).then(() => toast.success("Chave Pix copiada"));
  return (
    <TableRow data-testid={`admin-payout-${p.id}`}>
      <TableCell><p className="font-medium">{p.name}</p><Badge variant="outline" className="mt-0.5 text-[10px]">{KIND[p.kind] || p.kind}</Badge></TableCell>
      <TableCell>{p.pix_key ? <button type="button" onClick={copy} className="inline-flex items-center gap-1 font-mono text-xs hover:text-primary" data-testid={`admin-payout-pix-${p.id}`}>{p.pix_key}<Copy className="h-3 w-3" /></button> : <span className="text-xs text-amber-600">Sem chave Pix</span>}</TableCell>
      <TableCell className="text-sm">{p.period_label}</TableCell>
      <TableCell>{p.count}</TableCell>
      <TableCell className="font-mono font-semibold">{brl(p.amount)}</TableCell>
      <TableCell className="text-right">
        {p.status === "paid" ? (
          <div className="flex items-center justify-end gap-2"><Badge className="bg-emerald-600 hover:bg-emerald-600">Pago em {fmtDate(p.paid_at)}</Badge>
            <Button size="sm" variant="ghost" onClick={() => onMark(p, false)} data-testid={`admin-payout-undo-${p.id}`}>Desfazer</Button></div>
        ) : <Button size="sm" onClick={() => onMark(p, true)} data-testid={`admin-payout-mark-${p.id}`}>Marcar como pago</Button>}
      </TableCell>
    </TableRow>
  );
}

export function ReferralPayouts() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const load = useCallback(() => adminApi.get("/referrals/payouts", { params: { status } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), [status]);
  useEffect(() => { load(); }, [load]);
  const mark = async (p, paid) => {
    if (paid && !window.confirm(`Confirmar que você pagou ${brl(p.amount)} via Pix para ${p.name}?`)) return;
    try { await adminApi.post(`/referrals/payouts/${p.id}/paid`, { paid }); toast.success(paid ? "Marcado como pago" : "Pagamento desfeito"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const pending = rows.filter((r) => r.status === "pending").reduce((s, r) => s + r.amount, 0);
  return (
    <div className="card-surface overflow-x-auto" data-testid="admin-referral-payouts">
      <div className="flex flex-wrap items-center gap-2 p-5">
        <div className="flex-1"><p className="eyebrow">Fechamentos para pagar</p><p className="text-sm text-muted-foreground">A pagar nesta lista: <b className="font-mono" data-testid="admin-payouts-pending-total">{brl(pending)}</b></p></div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-md border bg-transparent px-2 text-sm" data-testid="admin-payouts-status-filter">
          <option value="pending">Aguardando pagamento</option><option value="paid">Pagos</option><option value="">Todos</option>
        </select>
        <Button variant="outline" size="icon" onClick={load} aria-label="Atualizar" data-testid="admin-payouts-refresh-btn"><RefreshCw className="h-4 w-4" /></Button>
      </div>
      {!rows.length ? <p className="px-5 pb-8 text-center text-sm text-muted-foreground">Nenhum fechamento {status === "pending" ? "aguardando pagamento" : "encontrado"}.</p> : (
        <Table>
          <TableHeader><TableRow><TableHead>Indicador</TableHead><TableHead>Chave Pix</TableHead><TableHead>Período</TableHead><TableHead>Comissões</TableHead><TableHead>Valor</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>{rows.map((p) => <PayoutRow key={p.id} p={p} onMark={mark} />)}</TableBody>
        </Table>
      )}
    </div>
  );
}
